import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Divider,
  Alert,
} from "@mui/material";

// Confirms splitting a WEEKEND group into per-staff sub-groups. `plan` comes from
// planWeekendSplit — the dialog just previews the resulting groups and fires the
// supplied onConfirm, which does the (non-atomic) create + reassign on the board.
export default function SplitGroupDialog({ open, onClose, groupName, plan, onConfirm }) {
  const [saving, setSaving] = useState(false);

  if (!plan) return null;

  const { summary } = plan;

  const handleConfirm = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onConfirm?.(plan);
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Split “{groupName}”</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Splits into {plan.subGroups.length + 1} groups, evening out the students
            and keeping booking groups together where possible.
          </Typography>

          {summary.maxGroupSize > 10 && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Not enough staff to keep every group at 10 students or fewer — the
              largest will have {summary.maxGroupSize}. You can still split.
            </Alert>
          )}

          <Divider />

          <Typography variant="body2">
            <strong>{summary.leaderName}</strong> keeps {summary.leaderAdults} adult
            {summary.leaderAdults === 1 ? "" : "s"} and {summary.leaderStudents} student
            {summary.leaderStudents === 1 ? "" : "s"}
          </Typography>

          {summary.subs.map((s, i) => (
            <Typography key={`${s.name}-${i}`} variant="body2">
              <strong>{s.name}</strong> leads {s.students} student
              {s.students === 1 ? "" : "s"}
            </Typography>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={saving}>
          {saving ? "Splitting…" : "Split group"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
